import { useState, useEffect, useMemo } from 'react';
import Spinner from '../Spinner';
import { useApi } from '../../api/ApiProvider';
import Api from '../../axiosApi/api';
import { PermissionsGate } from 'components/authorization/PermissionsGate';
import { SCOPES, ROLES } from "../../common/permissions";
import { Workflow } from 'axiosApi/models';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import intl from 'components/i18n/ReactIntlWrapper';
import { handleAPIError } from 'common/errorHandler';
import ErrorSummary from 'components/common/ErrorSummary';
import { DataGrid, GridColDef, GridInitialState } from '@mui/x-data-grid';
import { DEFAULT_PAGINATION, useSearch } from 'hooks/useSearch';
import SearchInput from 'components/SearchInput';
import { Box, Button, Typography, Card } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router';
import { getUserEntity } from 'common/UserEntityProvider';
import DefaultActions from 'components/DataGrid/DefaultActions';

const Workflows = () => {
  const api: Api = useApi();
  const navigate = useNavigate();
  const entityType = getUserEntity();
  
  const [errors, setErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState<string>('');

  const toast = useToastMessageQueue();

  const apiPromise = (
    searchTerm: string,
    pageNum: number,
    pageSize: number,
    sort: string,
    order: string
  ) => {
    if (searchTerm === "") {
      return api.workflowApi.apiVversionWorkflowAllGet("1", pageNum, pageSize, sort, order);
    } else {
      return api.workflowApi.apiVversionWorkflowSearchGet("1", searchTerm, pageNum, pageSize, sort, order);
    }
  };

  const processError = (error: any) => {
    handleAPIError(error, toast, errors);
    setErrors({ ...errors });
  };

  const { data, loading, onPaginationModelChange, handleSortModelChange, paginationModel } = useSearch<Workflow>({
    apiPromise: apiPromise,
    searchTerm: searchTerm,
    defaultSort: { sort: 'Name', order: 'asc' },
    onError: processError,
    pageKey: 'workflows',
  });

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const INITIAL_STATE: GridInitialState = {
    pagination: {
      paginationModel: {
        page: DEFAULT_PAGINATION.pageNum,
        pageSize: DEFAULT_PAGINATION.pageSize,
      },
    },
    sorting: {
      sortModel: [
        {
          field: 'name',
          sort: 'asc',
        },
      ],
    },
  };

  const columns: GridColDef[] = useMemo(() => [
    {
      field: 'name',
      headerName: intl.get('workflowList.table.thead.name'),
      width: 250,
      flex: 1,
    },
    {
      field: 'module',
      headerName: intl.get('workflowList.table.thead.module'),
      width: 200,
      valueGetter: (value, row) => row.module?.name || '',
    },
    {
      field: 'active',
      headerName: intl.get('workflowList.table.thead.active'),
      width: 120,
      type: 'boolean',
    },
    {
      field: 'actions',
      headerName: '',
      width: 150,
      renderCell: (params) => (
        <DefaultActions
          row={params.row}
          entityType={entityType.entityType}
          onEdit={() => navigate(`/workflow/${params.row.id}`)}
          editScopes={[SCOPES['workflows.edit']]}
        />
      ),
    },
  ], [entityType.entityType, navigate]);

  return (
    <Card sx={{ m: 2 }}>
    <Box sx={{ p: 3 }}>
      <Typography color="primary" variant="h2" component="h2" sx={{ mb: 2 }}>
        {intl.get('workflows.header')}
      </Typography>
      <Box>
        <ErrorSummary errors={errors} showEverything={true} />
        <PermissionsGate 
          viewScopes={[SCOPES['workflows.read']]} 
          editScopes={[SCOPES['workflows.edit']]} 
          viewRoles={null} 
          editRoles={null} 
          RenderError={()=>{return <span>{intl.get('permissionsGate')}</span>}}
        >
          <Box sx={{ p: 3 }}>
            <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} mb={2}>
              <SearchInput value={searchTerm} onChange={setSearchTerm} onClear={handleClearSearch} />
              <Button onClick={() => navigate(`/workflow/new`)}>
                <AddIcon /> {intl.get('workflow.new') ? intl.get('workflow.new') : intl.get('button.new')}
              </Button>
            </Box>
            <Box style={{ width: '100%', display: 'flex' }}>
              {loading && <Spinner />}
              {data && (
                <DataGrid
                  rows={data.queryResult || []}
                  columns={columns}
                  rowCount={data.totalRecords || 0}
                  loading={loading}
                  paginationModel={paginationModel}
                  onPaginationModelChange={onPaginationModelChange}
                  onSortModelChange={handleSortModelChange}
                  autoHeight
                />
              )}
            </Box>
          </Box>
        </PermissionsGate>
      </Box>
    </Box>
    </Card>
  );
};

export default Workflows;
